import { collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, query, orderBy, Timestamp, where } from 'firebase/firestore';
import { db } from '../firebase';

export interface NewsItem {
  id: string;
  title: string;
  description: string; // Can be plain text or HTML
  imageUrl?: string;
  linkUrl?: string;
  author?: string;
  tags?: string[];
  isRichText?: boolean; // Indicates if description contains HTML
  type: 'NEW RELEASE' | 'UPDATE' | 'FEATURE' | 'ANNOUNCEMENT';
  isActive: boolean;
  order?: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

const COLLECTION_NAME = 'news_items';

export async function getNewsItems(): Promise<NewsItem[]> {
  const q = query(collection(db, COLLECTION_NAME), orderBy('order', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<NewsItem, 'id'>) }));
}

export async function getActiveNewsItems(): Promise<NewsItem[]> {
  // Get all news items and filter/sort in memory to avoid composite index requirement
  const allItems = await getNewsItems();
  const activeItems = allItems
    .filter(item => item.isActive === true)
    .sort((a, b) => {
      // Sort by order first, then by createdAt (newest first) if order is the same
      const orderDiff = (a.order || 0) - (b.order || 0);
      if (orderDiff !== 0) return orderDiff;
      // If order is the same, sort by creation date (newest first)
      const aTime = a.createdAt?.toMillis?.() || 0;
      const bTime = b.createdAt?.toMillis?.() || 0;
      return bTime - aTime;
    });
  return activeItems;
}

export async function getNewsItem(id: string): Promise<NewsItem | null> {
  const ref = doc(db, COLLECTION_NAME, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<NewsItem, 'id'>) };
}

export async function createNewsItem(data: Omit<NewsItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
  const now = Timestamp.now();
  const ref = await addDoc(collection(db, COLLECTION_NAME), { ...data, createdAt: now, updatedAt: now });
  return ref.id;
}

export async function updateNewsItem(id: string, data: Partial<Omit<NewsItem, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> {
  const ref = doc(db, COLLECTION_NAME, id);
  await updateDoc(ref, { ...data, updatedAt: Timestamp.now() });
}

export async function deleteNewsItem(id: string): Promise<void> {
  const ref = doc(db, COLLECTION_NAME, id);
  await deleteDoc(ref);
}

